import { useCallback, useEffect, useState } from "react";
import { storage } from "utils/storage";
import { useUser } from "./useUser";

/**
 * How long the "this is new" dot stays up for a user who never opens the menu.
 * Long enough to survive a few sessions, short enough that the header settles
 * back to its normal state for anyone who is past onboarding.
 */
export const HELP_HINT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/** Keyed per user so a shared browser doesn't inherit someone else's dismissal. */
const storageKey = (userId: number) => `help-menu-hint-${userId}`;

interface HintRecord {
	/** ISO timestamp of the first render where we showed this user the header. */
	firstSeenAt: string;
	/** Set once the user opens the menu — the hint has done its job. */
	dismissed?: boolean;
}

/**
 * Owns the one-time "new" affordance on the header's help-and-feedback button.
 *
 * Deliberately localStorage-backed rather than a server preference (unlike
 * `reviewPromptSnoozedUntil`): a discovery hint is not worth a migration and a
 * new endpoint, and the worst failure mode — the dot reappearing once on a
 * second device — is a dot, not a lost dismissal.
 *
 * The window is measured from first sight rather than from `user.createdOn`, so
 * it also introduces the button to existing accounts, who are just as new to it
 * as a fresh signup is. Either way it expires on its own.
 */
export function useHelpMenuHint() {
	const { user } = useUser();
	const userId = user?.id;
	const [showHint, setShowHint] = useState(false);

	useEffect(() => {
		if (!userId) {
			setShowHint(false);
			return;
		}
		const key = storageKey(userId);
		const record = storage.getItem<HintRecord>(key);

		if (!record) {
			// First sight: start the clock and show the dot.
			storage.setItem(key, { firstSeenAt: new Date().toISOString() });
			setShowHint(true);
			return;
		}
		if (record.dismissed) {
			setShowHint(false);
			return;
		}

		const firstSeenAt = Date.parse(record.firstSeenAt);
		// An unreadable timestamp reads as "expired". Suppressing a hint is the
		// safe direction; a dot that can never clear itself is not.
		if (Number.isNaN(firstSeenAt)) {
			setShowHint(false);
			return;
		}
		setShowHint(Date.now() - firstSeenAt < HELP_HINT_WINDOW_MS);
	}, [userId]);

	const dismissHint = useCallback(() => {
		if (!userId) return;
		setShowHint(false);
		const key = storageKey(userId);
		const record = storage.getItem<HintRecord>(key);
		storage.setItem(key, {
			firstSeenAt: record?.firstSeenAt ?? new Date().toISOString(),
			dismissed: true,
		});
	}, [userId]);

	return { showHint, dismissHint };
}
